import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// Rôles considérés comme membres du personnel (tout sauf patient)
const STAFF_ROLES = [
  'super_admin',
  'admin',
  'comptable',
  'medecin',
  'secretaire',
  'laborantin',
  'sage_femme',
  'infirmier',
];

// Page d'accueil propre à chaque rôle du personnel
function getStaffHome(role: string): string {
  switch (role) {
    case 'super_admin':
    case 'admin':
      return '/admin';
    case 'medecin':
      return '/medecin/rendez-vous';
    case 'secretaire':
      return '/secretaire/dashboard';
    case 'laborantin':
      return '/laborantin/dashboard';
    case 'sage_femme':
      return '/sage-femme/dashboard';
    case 'infirmier':
      return '/infirmier/dashboard';
    case 'comptable':
      return '/comptable/dashboard';
    default:
      return '/';
  }
}

// Pages publiques (marketing / grand public) que le personnel ne doit pas voir
function isPublicOnlyPath(pathname: string): boolean {
  const p = pathname || '/';

  if (p === '/' || p === '/services' || p === '/contact' || p === '/rendez-vous' || p === '/register') {
    return true;
  }
  if (p === '/actualites' || p.startsWith('/actualites/')) {
    return true;
  }
  if (p === '/jeux' || p.startsWith('/jeux/')) {
    return true;
  }

  const legalPages = [
    '/mentions-legales',
    '/politique-confidentialite',
    '/conditions-utilisation',
    '/gestion-cookies',
    '/charte-deontologie',
  ];
  return legalPages.includes(p);
}

/**
 * Garde global : dès qu'un membre du personnel (médecin, infirmier, sage-femme,
 * admin, secrétaire, laborantin, comptable) est authentifié et se trouve sur une
 * page publique, il est redirigé vers sa page d'accueil attitrée.
 * Les patients, eux, conservent l'accès aux pages publiques.
 */
export default function StaffRouteGuard() {
  const { user, role, loading, rolesLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || rolesLoading) return;
    if (!user || !role) return;
    if (!STAFF_ROLES.includes(role)) return;
    if (!isPublicOnlyPath(location.pathname)) return;

    navigate(getStaffHome(role), { replace: true });
  }, [user, role, loading, rolesLoading, location.pathname, navigate]);

  return null;
}